import { ActionCreator } from 'redux';
import { Template, TemplateScopeType, TemplateOrder, TemplateType, LandingPage, LandingPageTypeEnum } from '@dev/ee-api/ee-api-models';

import {
    LoadListErrorAction,
    LoadListStartAction,
    LoadListSuccessAction,
    templateDeleteStartAction,
    templateDeleteSuccessAction,
    templateDeleteErrorAction,
    templatePreviewStartAction,
    templatePreviewErrorAction,
    templatePreviewSuccessAction,
    templateUpdateSuccessAction,
    templateUpdateStartAction,
    templateUpdateErrorAction,
    SaveTemplateStartAction,
    SaveTemplateSuccessAction,
    SaveTemplateErrorAction,
    templateArrayDeleteStartAction,
    templateArrayDeleteSuccessAction,
    templateArrayDeleteErrorAction,
    downloadLandingPageStartAction,
    downloadLandingPageSuccessAction,
    downloadLandingPageErrorAction,
    TemplateListState,
    LoadAlreadySubscribedLandingPagesErrorAction,
    LoadAlreadySubscribedLandingPagesListStartAction,
    LoadAlreadySubscribedLandingPagesSuccessAction,
    LoadSubmitLandingPagesErrorAction,
    LoadSubmitLandingPagesStartAction,
    LoadSubmitLandingPagesSuccessAction,
    LandingPagesActionTypes,
    LoadTemplateSuccess,
    LoadTemplateStart,
    LoadTemplateError,
    LoadTemplateSubmitSuccess,
    LoadTemplateSubmitStart,
    LoadTemplateSubmitError,
    LoadTemplateAlreadySubscribedSuccess,
    LoadTemplateAlreadySubscribedStart,
    LoadTemplateAlreadySubscribedError,
    PageTypeEnum,
    LoadLandingPagesListStartAction,
    LoadLandingPagesListSuccessAction,
    LoadLandingPagesListErrorAction,
} from './types';

import { ApplicationState } from '..';

export interface StartLoadingTemplateParams {
    page: number;
    limit: number;
    templateOrder?: TemplateOrder;
    search?: string;
    type: TemplateScopeType[];
    tags?: string[];
    templateTypes?: TemplateType[];
    isInfinityScroll?: boolean;
    pageType?: PageTypeEnum;
}

export const StartLoadingTemplate = (name: string): LoadTemplateStart => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_START,
    payload: {
        name,
        pageType: PageTypeEnum.DoubleOptIn
    }
});

export const LoadingTemplateSuccess = (template: Template): LoadTemplateSuccess => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_SUCCESS,
    payload: {
        template
    }
});
export const LoadingTemplateError = (): LoadTemplateError => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_ERROR,
});
export const StartLoadingSubmitTemplate = (name: string): LoadTemplateSubmitStart => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_SUBMIT_START,
    payload: {
        name,
        loadingTemplateFormSubmit: true,
        pageType: PageTypeEnum.FormSubmit
    }
});

export const LoadingTemplateSubmitSuccess = (templateFormSubmit: Template): LoadTemplateSubmitSuccess => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_SUBMIT_SUCCESS,
    payload: {
        templateFormSubmit,
        loadingTemplateFormSubmit: false
    }
});
export const LoadingTemplateSubmitError = (): LoadTemplateSubmitError => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_SUBMIT_ERROR,
    payload: {
        loadingTemplateFormSubmit: false
    }
});
export const StartLoadingTemplateAlreadySubscribed = (name: string): LoadTemplateAlreadySubscribedStart => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_ALREADY_SUBSCRIBED_START,
    payload: {
        name,
        loadingTemplateAlreadySubscribed: true,
        pageType: PageTypeEnum.AlreadySubscribed
    }
});

export const LoadingTemplateAlreadySubscribedSuccess = (templateAlreadySubscribed: Template): LoadTemplateAlreadySubscribedSuccess => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_ALREADY_SUBSCRIBED_SUCCESS,
    payload: {
        templateAlreadySubscribed,
        loadingTemplateAlreadySubscribed: false
    }
});
export const LoadingTemplateAlreadySubscribedError = (): LoadTemplateAlreadySubscribedError => ({
    type: LandingPagesActionTypes.LOADING_LANDING_PAGE_ALREADY_SUBSCRIBED_ERROR,
    payload: {
        loadingTemplateAlreadySubscribed: false
    }
});

export const StartLoadingTemplates: ActionCreator<LoadListStartAction> = (
    params: StartLoadingTemplateParams
): LoadListStartAction => ({
    type: '@@landingPageList/LOADING_START',
    payload: {
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        isInfinityScroll: params.isInfinityScroll,
    },
});

export const LoadingTemplatesSuccess: ActionCreator<LoadListSuccessAction> = (
    list: Template[],
    count: number,
    hasMoreTemplates?: boolean,
): LoadListSuccessAction => ({
    type: '@@landingPageList/LOADING_SUCCESS',
    payload: {
        loadingTemplates: false,
        templatesList: list,
        count,
        hasMoreTemplates
    },
});

export const LoadingTemplatesError: ActionCreator<LoadListErrorAction> = (): LoadListErrorAction => ({
    type: '@@landingPageList/LOADING_ERROR',
    payload: {
        loadingTemplates: false,
    },
});

export const StartLoadingSubmitLandingPages = (
    params: StartLoadingTemplateParams
): LoadSubmitLandingPagesStartAction => ({
    type: LandingPagesActionTypes.LOADING_SUBMIT_LANDING_PAGES_START,
    payload: {
        loadingSubmitLandingPages: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        isInfinityScroll: params.isInfinityScroll,
        pageType: params.pageType, 
    },
});

export const LoadingSubmitLandingPagesSuccess = (
    list: Template[],
    hasMoreSubmitPages?: boolean,
): LoadSubmitLandingPagesSuccessAction => ({
    type: LandingPagesActionTypes.LOADING_SUBMIT_LANDING_PAGES_SUCCESS,
    payload: {
        loadingSubmitLandingPages: false,
        submitLandingPagesList: list,
        hasMoreSubmitPages
    },
});

export const LoadingSubmitLandingPagesError = (): LoadSubmitLandingPagesErrorAction => ({
    type: LandingPagesActionTypes.LOADING_SUBMIT_LANDING_PAGES_ERROR,
    payload: {
        loadingSubmitLandingPages: false,
    },
});

export const StartLoadingAlreadySubscribedLandingPages = (
    params: StartLoadingTemplateParams
): LoadAlreadySubscribedLandingPagesListStartAction => ({
    type: LandingPagesActionTypes.LOADING_ALREADY_SUBSCRIBED_LANDING_PAGES_START,
    payload: {
        loadingAlreadySubscribedLandingPages: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        isInfinityScroll: params.isInfinityScroll,
    },
});

export const LoadingAlreadySubscribedLandingPagesSuccess = (
    list: Template[],
    hasMoreAlreadySubscribedPages?: boolean,
): LoadAlreadySubscribedLandingPagesSuccessAction => ({
    type: LandingPagesActionTypes.LOADING_ALREADY_SUBSCRIBED_LANDING_PAGES_SUCCESS,
    payload: {
        loadingAlreadySubscribedLandingPages: false,
        alreadySubscribedLandingPagesList: list,
        hasMoreAlreadySubscribedPages
    },
});

export const LoadingAlreadySubscribedLandingPagesError = (): LoadAlreadySubscribedLandingPagesErrorAction => ({
    type: LandingPagesActionTypes.LOADING_ALREADY_SUBSCRIBED_LANDING_PAGES_ERROR,
    payload: {
        loadingAlreadySubscribedLandingPages: false,
    },
});

export const downloadLandingPageStart: ActionCreator<downloadLandingPageStartAction> = (
    name: string
): downloadLandingPageStartAction => ({
    type: '@@landingPageList/DOWNLOAD_LANDING_PAGE_START',
    payload: {
        name,
    },
});

export const downloadLandingPageSuccess: ActionCreator<downloadLandingPageSuccessAction> = (
    name: string
): downloadLandingPageSuccessAction => ({
    type: '@@landingPageList/DOWNLOAD_LANDING_PAGE_SUCCESS',
    payload: {
        name,
    },
});

export const downloadLandingPageError: ActionCreator<
    downloadLandingPageErrorAction
> = (): downloadLandingPageErrorAction => ({
    type: '@@landingPageList/DOWNLOAD_LANDING_PAGE_ERROR',
    payload: {},
});

export const templateArrayDeleteStart: ActionCreator<templateArrayDeleteStartAction> = (
    templatesToDelete: string[],
    params: StartLoadingTemplateParams
): templateArrayDeleteStartAction => ({
    type: '@@landingPageList/DELETE_ARRAY_START',
    payload: {
        templatesToDelete,
        DeletingTemplate: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        loadingTemplates: true,
    },
});
export const templateArrayDeleteSuccess: ActionCreator<templateArrayDeleteSuccessAction> = (
    list: Template[],
    count: number,
    params: StartLoadingTemplateParams
): templateArrayDeleteSuccessAction => ({
    type: '@@landingPageList/DELETE_ARRAY_SUCCESS',
    payload: {
        DeletingTemplate: false,
        templatesList: list,
        count,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        loadingTemplates: false,
    },
});
export const templateArrayDeleteError: ActionCreator<
    templateArrayDeleteErrorAction
> = (): templateArrayDeleteErrorAction => ({
    type: '@@landingPageList/DELETE_ARRAY_ERROR',
    payload: {
        DeletingTemplate: false,
        loadingTemplates: false,
    },
});
export const templateDeleteStart: ActionCreator<templateDeleteStartAction> = (
    name: string,
    params: StartLoadingTemplateParams
): templateDeleteStartAction => ({
    type: '@@landingPageList/DELETE_START',
    payload: {
        name,
        DeletingTemplate: true,
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
    },
});
export const templateDeleteSuccess: ActionCreator<templateDeleteSuccessAction> = (
    name: string,
    list: Template[],
    count: number,
    params: StartLoadingTemplateParams
): templateDeleteSuccessAction => ({
    type: '@@landingPageList/DELETE_SUCCESS',
    payload: {
        name,
        DeletingTemplate: false,
        templatesList: list,
        count,
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
    },
});
export const templateDeleteError: ActionCreator<templateDeleteErrorAction> = (): templateDeleteErrorAction => ({
    type: '@@landingPageList/DELETE_ERROR',
    payload: {
        DeletingTemplate: false,
    },
});
export const TemplatePreviewStart: ActionCreator<templatePreviewStartAction> = (
    name: string,
    ispublic?: boolean
): templatePreviewStartAction => ({
    type: '@@landingPageList/PREVIEW_START',
    payload: {
        name,
        ispublic,
        PreviewLoading: true,
    },
});
export const templatePreviewSuccess: ActionCreator<templatePreviewSuccessAction> = (
    name: string,
    template: Template,
    ispublic?: boolean
): templatePreviewSuccessAction => ({
    type: '@@landingPageList/PREVIEW_SUCCESS',
    payload: {
        name,
        template,
        ispublic,
        PreviewLoading: false,
    },
});
export const templatePreviewError: ActionCreator<templatePreviewErrorAction> = (): templatePreviewErrorAction => ({
    type: '@@landingPageList/PREVIEW_ERROR',
    payload: {
        PreviewLoading: false,
    },
});
export interface StartUpdatingTemplateParams {
    templateName: string;
    template: Template;
    removeScreenshot: boolean;
}
export const templateUpdateStart: ActionCreator<templateUpdateStartAction> = (
    params: StartUpdatingTemplateParams
): templateUpdateStartAction => ({
    type: '@@landingPageList/UPDATE_START',
    payload: {
        name: params.templateName,
        template: params.template,
        removeScreenshot: params.removeScreenshot,
    },
});
export const templateUpdateSuccess: ActionCreator<templateUpdateSuccessAction> = (
    name: string,
    list: Template[]
): templateUpdateSuccessAction => ({
    type: '@@landingPageList/UPDATE_SUCCESS',
    payload: {
        name,
        UpdatingTemplate: false,
        templatesList: list,
    },
});
export const templateUpdateError: ActionCreator<templateUpdateErrorAction> = (): templateUpdateErrorAction => ({
    type: '@@landingPageList/UPDATE_ERROR',
    payload: {
        UpdatingTemplate: false,
    },
});
export const AddTemplateStart: ActionCreator<SaveTemplateStartAction> = (
    template: Template,
    prevTemplateName?: string,
    changes?: Template
): SaveTemplateStartAction => ({
    type: '@@landingPageList/SAVE_TEMPLATE_START',
    payload: {
        templateSaving: true,
        template,
        prevTemplateName,
        changes,
    },
});

export const AddTemplateSuccess: ActionCreator<SaveTemplateSuccessAction> = (
    templateObject: Template,
    list: Template[],
    count: number
): SaveTemplateSuccessAction => ({
    type: '@@landingPageList/SAVE_TEMPLATE_SUCCESS',
    payload: {
        templateObject,
        templateSaving: false,
        templatesList: list,
        count,
    },
});

export const AddTemplateError: ActionCreator<SaveTemplateErrorAction> = (): SaveTemplateErrorAction => ({
    type: '@@landingPageList/SAVE_TEMPLATE_ERROR',
    payload: {
        templateSaving: false,
    },
});

export const LoadLandingPagesListStart = (type: LandingPageTypeEnum, offset: number, limit: number): LoadLandingPagesListStartAction => ({
    type: LandingPagesActionTypes.LOAD_LANDING_PAGES_LIST_START,
    payload: { type, offset, limit },
});

export const LoadLandingPagesListSuccess = (landingPagesList: LandingPage[]): LoadLandingPagesListSuccessAction => ({
    type: LandingPagesActionTypes.LOAD_LANDING_PAGES_LIST_SUCCESS,
    payload: { landingPagesList },
});

export const LoadLandingPagesListError = (): LoadLandingPagesListErrorAction => ({
    type: LandingPagesActionTypes.LOAD_LANDING_PAGES_LIST_ERROR,
});

export const getLandingPageList = (store: ApplicationState): TemplateListState => store?.landingPageList;